import { AnalyticIdentifier } from '@common/neo.tax';
import { Stack, ControlledInput, ControlledRadioGroup } from '@ui-components/neo.tax';
import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  useAuthErrorMessage,
  useDomainRedirect,
  useProvisionCurrentUser,
  useReportLoginFailure,
} from '@neo.tax/onboarding/hooks';
import { StyledGeneralFormError, SubmitButton } from '@neo.tax/onboarding/organisms';
import {
  AuthErrorType,
  AuthenticationState,
  LocalStorageKey,
  SsoProvider,
  startOAuth,
  useAnalyticId,
} from '@neo.tax/shared';

import { useDefaultSsoProviderId } from '../../hooks/useDefaultSsoProviderId';
import { EnterpriseSsoFormValues } from './enterprise-sso-form.types';

const SSO_PROVIDER_TYPE_OPTIONS = [
  {
    id: SsoProvider.OIDC,
    value: SsoProvider.OIDC,
    label: 'OIDC',
  },
  {
    id: SsoProvider.SAML,
    value: SsoProvider.SAML,
    label: 'SAML',
  },
];

export const EnterpriseSsoForm = (): React.ReactElement => {
  const providerIdInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_PROVIDER_ID_INPUT);
  const submitProviderIdButtonId = useAnalyticId(AnalyticIdentifier.ONBOARDING_SUBMIT_PROVIDER_ID_BUTTON);
  const ssoProviderTypeInputId = useAnalyticId(AnalyticIdentifier.ONBOARDING_SSO_PROVIDER_TYPE_INPUT);

  const { defaultSsoProviderId } = useDefaultSsoProviderId();
  const { getRedirectUrlForEmail } = useDomainRedirect();
  const provisionCurrentUser = useProvisionCurrentUser();
  const reportLoginFailure = useReportLoginFailure();

  const { control, formState, handleSubmit, watch } = useForm<EnterpriseSsoFormValues>({
    mode: 'onChange',
    defaultValues: {
      enterpriseSsoProviderIdSuffix: defaultSsoProviderId.substring(5), // remove the oidc. or saml. prefix to get the suffix
      ssoProviderType: defaultSsoProviderId.startsWith('saml.') ? SsoProvider.SAML : SsoProvider.OIDC,
    },
  });

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  // The reason a sign-in failed is shared state, written by whichever attempt failed; this hook turns
  // whatever is there into the copy to show, so the same failure is worded identically everywhere.
  const [errorMessage] = useAuthErrorMessage();

  // Releases the form when a sign-in fails, on top of the shared bookkeeping. Composed rather than
  // replaced so the reason still reaches the shared state this page reads it back from.
  const oauthAdapter = useMemo(
    () => ({
      ...AuthenticationState.authenticationStateOAuthAdapter,
      onError: (reason?: AuthErrorType): void => {
        AuthenticationState.authenticationStateOAuthAdapter.onError?.(reason);
        setIsSubmitted(false);
      },
    }),
    [],
  );

  const selectedProviderType = watch('ssoProviderType');

  // Runs only from the Continue click: startOAuth opens the provider popup, which browsers block without a user gesture.
  const onSubmit = useCallback(
    async ({ enterpriseSsoProviderIdSuffix }: EnterpriseSsoFormValues): Promise<void> => {
      setIsSubmitted(true);
      const enterpriseSsoProviderId = `${selectedProviderType.toLowerCase()}.${enterpriseSsoProviderIdSuffix.trim()}`;
      localStorage.setItem(LocalStorageKey.PERSIST_ENTERPRISE_SSO_PROVIDER_ID, enterpriseSsoProviderId);
      await startOAuth({
        ssoProvider: selectedProviderType,
        adapter: oauthAdapter,
        enterpriseSsoProviderId,
        getSingleTenantRedirectUrl: getRedirectUrlForEmail,
        provisionCurrentUser,
        reportLoginFailure,
      });
    },
    [selectedProviderType, oauthAdapter, getRedirectUrlForEmail, provisionCurrentUser, reportLoginFailure],
  );

  return (
    <Stack as="form" direction="column" paddingY={6} onSubmit={handleSubmit(onSubmit)}>
      <ControlledRadioGroup
        control={control}
        name="ssoProviderType"
        rules={{ required: true }}
        id={ssoProviderTypeInputId}
        label="Provider Type"
        options={SSO_PROVIDER_TYPE_OPTIONS}
      />
      <ControlledInput
        control={control}
        name="enterpriseSsoProviderIdSuffix"
        rules={{
          required: true,
        }}
        id={providerIdInputId}
        label="Provider ID"
        placeholder="your-provider-id"
      />
      <Stack direction="column" gap={5}>
        <SubmitButton
          disabled={!formState.isValid || isSubmitted}
          variant="primary"
          rounded="regular"
          size="base"
          type="submit"
          id={submitProviderIdButtonId}
        >
          Continue with SSO
        </SubmitButton>
      </Stack>
      <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
    </Stack>
  );
};
